import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Mail, User, Phone, Sparkles } from "lucide-react";

interface WaitlistDialogProps {
  trigger?: React.ReactNode;
}

export const WaitlistDialog = ({ trigger }: WaitlistDialogProps) => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name || !formData.email || !formData.phone) {
      toast({
        title: t('waitlist.allFieldsRequired'),
        description: t('waitlist.fillAllFields'),
        variant: "destructive",
      });
      return;
    }

    setLoading(true);

    try {
      const { error } = await supabase
        .from("waitlist_signups")
        .insert({
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
        });

      if (error) {
        if (error.code === '23505') {
          toast({
            title: t('waitlist.alreadyRegistered'),
            description: t('waitlist.alreadyOnWaitlist'),
          });
        } else {
          throw error;
        }
      } else {
        toast({
          title: t('waitlist.success'),
          description: t('waitlist.addedToWaitlist'),
        });
        setFormData({ name: "", email: "", phone: "" });
        setOpen(false);
      }
    } catch (error) {
      console.error("Error signing up:", error);
      toast({
        title: t('waitlist.error'),
        description: t('waitlist.somethingWrong'),
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger || (
          <Button size="lg" className="bg-gradient-accent hover:opacity-90">
            {t('waitlist.joinWaitlist')}
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-2xl">
            <Sparkles className="w-6 h-6 text-primary" />
            {t('waitlist.dialogTitle')}
          </DialogTitle>
          <DialogDescription>
            {t('waitlist.dialogDescription')}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          <div className="space-y-2">
            <Label htmlFor="waitlist-name">Full Name</Label>
            <div className="relative">
              <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                id="waitlist-name"
                name="name"
                type="text"
                placeholder="Enter your full name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="pl-10"
                disabled={loading}
                aria-label="Full Name"
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="waitlist-email">Email Address</Label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                id="waitlist-email"
                name="email"
                type="email"
                placeholder="Enter your email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="pl-10"
                disabled={loading}
                aria-label="Email Address"
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="waitlist-phone">Phone Number</Label>
            <div className="relative">
              <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                id="waitlist-phone"
                name="phone"
                type="tel"
                placeholder="Enter your phone number"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                className="pl-10"
                disabled={loading}
                aria-label="Phone Number"
              />
            </div>
          </div>
          <Button
            type="submit"
            className="w-full bg-gradient-accent hover:opacity-90"
            size="lg"
            disabled={loading}
          >
            {loading ? (
              <span className="flex items-center gap-2">
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                {t('waitlist.joining')}
              </span>
            ) : (
              t('waitlist.joinWaitlist')
            )}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
